import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogActions, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import { TablerIconsModule } from 'angular-tabler-icons';
import { DialogTone, dialogToneIcon } from 'src/app/core/services/dialog-shell-rules';

/**
 * The one dialog layout of the app: header (optional tone icon, title, optional close button),
 * body, and a row of full-width buttons. Dialogs opened by PopupAndNotifService are built on it;
 * a dedicated dialog (business data, form) should be too, so every popup looks the same.
 *
 * Usage, inside a component opened with MatDialog:
 *
 *   <app-dialog-shell title="Delete the fee?" tone="danger">
 *     <p>The fee is removed from the deal.</p>
 *     <ng-container dialogActions>
 *       <button mat-flat-button class="ap-dialog-btn ap-dialog-btn--neutral" [mat-dialog-close]="false">Cancel</button>
 *       <button mat-flat-button class="ap-dialog-btn ap-dialog-btn--danger" [mat-dialog-close]="true">Delete fee</button>
 *     </ng-container>
 *   </app-dialog-shell>
 *
 * Styles are global (ViewEncapsulation.None) and prefixed `ap-dialog-`, so projected buttons pick
 * them up; colors come only from the design tokens (--ap-*), light and dark.
 * Mockups: docs/ArturPro/UI_DESIGN_SYSTEM.md.
 */
@Component({
  selector: 'app-dialog-shell',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [MatDialogTitle, MatDialogContent, MatDialogActions, MatButtonModule, TablerIconsModule],
  template: `
    <div class="ap-dialog-header">
      @if (headerIcon) {
        <span class="ap-dialog-icon" [class]="'ap-dialog-icon ap-dialog-icon--' + tone" aria-hidden="true">
          <i-tabler [name]="headerIcon" class="icon-20 d-flex"></i-tabler>
        </span>
      }
      <h2 mat-dialog-title class="ap-dialog-title">{{ title }}</h2>
      @if (closable) {
        <button mat-icon-button type="button" class="ap-dialog-close" [attr.aria-label]="closeLabel" (click)="closeClick.emit()">
          <i-tabler name="x" class="icon-20 d-flex"></i-tabler>
        </button>
      }
    </div>

    <mat-dialog-content class="ap-dialog-body">
      <ng-content></ng-content>
    </mat-dialog-content>

    <mat-dialog-actions class="ap-dialog-actions" [class.ap-dialog-actions--reversed]="reverseActions">
      <ng-content select="[dialogActions]"></ng-content>
    </mat-dialog-actions>
  `,
  styles: [`
    .ap-dialog-header {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 22px 24px 0;
      min-width: 0;
    }

    .ap-dialog-title.mat-mdc-dialog-title {
      flex: 1;
      min-width: 0;
      margin: 0;
      padding: 0;
      font-size: 1.1rem;
      font-weight: 700;
      line-height: 1.35;
      color: var(--ap-color-text);
      overflow-wrap: anywhere;
    }

    .ap-dialog-title.mat-mdc-dialog-title::before {
      display: none;
    }

    .ap-dialog-icon {
      flex: 0 0 auto;
      width: 40px;
      height: 40px;
      border-radius: var(--ap-radius);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .ap-dialog-icon--default,
    .ap-dialog-icon--info {
      background: var(--ap-color-primary-light);
      color: var(--ap-color-primary);
    }

    .ap-dialog-icon--danger {
      background: var(--ap-color-danger-light);
      color: var(--ap-color-danger);
    }

    .ap-dialog-icon--warning {
      background: var(--ap-color-warning-light);
      color: var(--ap-color-warning-ink);
    }

    .ap-dialog-icon--success {
      background: var(--ap-color-success-light);
      color: var(--ap-color-success-ink);
    }

    .ap-dialog-close {
      flex: 0 0 auto;
      margin: -8px -12px 0 0;
      color: var(--ap-color-text-muted);
    }

    .ap-dialog-body.mat-mdc-dialog-content {
      padding: 14px 24px 8px !important;
      margin: 0;
      min-width: 0;
      font-size: 0.92rem;
      line-height: 1.55;
      color: var(--ap-color-text-message);
      overflow-wrap: anywhere;
    }

    .ap-dialog-actions.mat-mdc-dialog-actions {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: stretch;
      justify-content: flex-end;
      gap: 10px;
      padding: 8px 24px 22px !important;
      margin: 0;
      min-height: unset;
    }

    .ap-dialog-actions--reversed.mat-mdc-dialog-actions {
      flex-direction: row-reverse;
    }

    .ap-dialog-actions:empty {
      display: none;
    }

    .ap-dialog-btn.mat-mdc-button-base {
      flex: 1 1 0;
      min-width: 0;
      min-height: 42px;
      height: auto;
      padding: 10px 16px;
      border-radius: var(--ap-radius-button-dialog);
      font-size: 0.86rem;
      font-weight: 600;
      line-height: 1.35;
      white-space: normal;
    }

    .ap-dialog-btn--primary.mat-mdc-button-base:not(:disabled) {
      background: var(--ap-color-primary);
      color: var(--ap-color-on-primary);
    }

    .ap-dialog-btn--danger.mat-mdc-button-base:not(:disabled) {
      background: var(--ap-color-danger);
      color: #ffffff;
    }

    .ap-dialog-btn--neutral.mat-mdc-button-base:not(:disabled) {
      background: transparent;
      color: var(--ap-color-text);
      box-shadow: inset 0 0 0 1px var(--ap-color-border);
    }

    .ap-dialog-btn--light.mat-mdc-button-base:not(:disabled) {
      background: var(--ap-color-primary-light);
      color: var(--ap-color-primary);
    }
  `],
})
export class DialogShellComponent {
  @Input({ required: true }) title = '';
  @Input() tone: DialogTone = 'default';
  /** Tabler icon name; when omitted, the tone's default icon (none for `default`). */
  @Input() icon: string | null = null;
  /** Shows a close button in the header; the host decides what closing means. */
  @Input() closable = false;
  @Input() closeLabel = 'Close';
  @Input() reverseActions = false;
  @Output() closeClick = new EventEmitter<void>();

  get headerIcon(): string | null {
    return dialogToneIcon(this.tone, this.icon);
  }
}
